import { FeeScheme, OnboardingProgramOverrideResponse, OnboardingResponse, Partners, PromoCodes, ProgramCodes } from '@mesa-labs/mesa-api/dist/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { setupCognito } from './auth';
import { SidebarTask } from './onboardingSidebar';

// Stop-gap for the deferred-onboarding A/B test readout: tracks how far a
// vendor got before a vendor record exists to key off of instead.
export enum OnboardingStage {
  BusinessInfo = 1,
  PaymentSchedule = 2,
  Bank = 3,
  DirectDebitAgreement = 4,
  FinalAgreement = 5,
}

// Scoped to an email so a mark left in persisted state by a previous vendor on a
// shared browser can't suppress the next vendor's writes.
export type LastCompletedStep = {
  email: string;
  stage: OnboardingStage;
};

interface OnboardingState {
  onboardingId?: string;
  customMsa: boolean;
  feeScheme?: FeeScheme;
  partnerId?: Partners;
  instantPayoutAlwaysOn?: boolean;
  programCode: ProgramCodes | undefined;
  promoCode: PromoCodes | undefined;
  skipAutoInstantPayoutStep?: boolean;
  requireInstantAccountVerification?: boolean;
  forceDirectDebitAgreement?: boolean;
  subdomain?: string;
  onboardingTask?: SidebarTask;
  availablePrograms?: readonly OnboardingProgramOverrideResponse[];
  lastCompletedStep?: LastCompletedStep;
  // Obsolete
  restrictToPrograms: OnboardingResponse['restrictToPrograms'];
  programOverrides: readonly OnboardingProgramOverrideResponse[];
  acceptedPriceSchedule: boolean;
}

const initialState: OnboardingState = {
  onboardingId: undefined,
  customMsa: false,
  feeScheme: FeeScheme.DISCOUNTING,
  partnerId: Partners.NO_PARTNER,
  instantPayoutAlwaysOn: false,
  programCode: undefined,
  promoCode: undefined,
  skipAutoInstantPayoutStep: false,
  requireInstantAccountVerification: false,
  forceDirectDebitAgreement: false,
  subdomain: undefined,
  onboardingTask: undefined,
  availablePrograms: undefined,
  lastCompletedStep: undefined,
  // Obsolete
  restrictToPrograms: [],
  programOverrides: [],
  acceptedPriceSchedule: false,
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    updateOnboardingId(state: OnboardingState, action: PayloadAction<string>) {
      state.onboardingId = action.payload;
    },
    updateCustomMsa(state: OnboardingState, action: PayloadAction<boolean>) {
      state.customMsa = action.payload;
    },
    updateFeeScheme(state: OnboardingState, action: PayloadAction<FeeScheme>) {
      state.feeScheme = action.payload;
    },
    updatePartnerId(state: OnboardingState, action: PayloadAction<Partners>) {
      state.partnerId = action.payload;
    },
    updateInstantPayoutAlwaysOn(state: OnboardingState, action: PayloadAction<boolean>) {
      state.instantPayoutAlwaysOn = action.payload;
    },
    updateProgramCode(state: OnboardingState, action: PayloadAction<ProgramCodes>) {
      state.programCode = action.payload;
    },
    updatePromoCode(state: OnboardingState, action: PayloadAction<PromoCodes | undefined>) {
      state.promoCode = action.payload;
    },
    updateSkipAutoInstantPayoutStep(state: OnboardingState, action: PayloadAction<boolean>) {
      state.skipAutoInstantPayoutStep = action.payload;
    },
    updateRequireInstantAccountVerification(state: OnboardingState, action: PayloadAction<boolean>) {
      state.requireInstantAccountVerification = action.payload;
    },
    updateForceDirectDebitAgreement(state: OnboardingState, action: PayloadAction<boolean>) {
      state.forceDirectDebitAgreement = action.payload;
    },
    updateSubdomain(state: OnboardingState, action: PayloadAction<string>) {
      state.subdomain = action.payload;
    },
    updateOnboardingTask(state: OnboardingState, action: PayloadAction<SidebarTask | undefined>) {
      state.onboardingTask = action.payload;
    },
    updateAvailablePrograms(state: OnboardingState, action: PayloadAction<readonly OnboardingProgramOverrideResponse[] | undefined>) {
      state.availablePrograms = action.payload;
    },
    updateLastCompletedStep(state: OnboardingState, action: PayloadAction<LastCompletedStep>) {
      state.lastCompletedStep = action.payload;
    },
    // Obsolete
    updateRestrictToPrograms(state: OnboardingState, action: PayloadAction<OnboardingResponse['restrictToPrograms']>) {
      state.restrictToPrograms = action.payload;
    },
    updateProgramOverrides(state: OnboardingState, action: PayloadAction<readonly OnboardingProgramOverrideResponse[]>) {
      state.programOverrides = action.payload;
    },
    updateAcceptedPriceSchedule(state: OnboardingState, action: PayloadAction<boolean>) {
      state.acceptedPriceSchedule = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setupCognito.fulfilled, (state: OnboardingState, action) => {
        // Only restore partnerId from JWT if there's no active onboarding flow or demo mode
        // Skip '0' (NO_PARTNER) as it's not valid for MSA generation or vendor creation
        if (action.payload.trackingPartnerId && action.payload.trackingPartnerId !== '0' && !state.onboardingId && !action.payload.isDemoMode) {
          state.partnerId = parseInt(action.payload.trackingPartnerId, 10);
        }
      });
  },
});

export const {
  updateOnboardingId,
  updateCustomMsa,
  updateFeeScheme,
  updatePartnerId,
  updateInstantPayoutAlwaysOn,
  updateProgramCode,
  updatePromoCode,
  updateSkipAutoInstantPayoutStep,
  updateRequireInstantAccountVerification,
  updateForceDirectDebitAgreement,
  updateSubdomain,
  updateOnboardingTask,
  updateAvailablePrograms,
  updateLastCompletedStep,
  // Obsolete
  updateRestrictToPrograms,
  updateProgramOverrides,
  updateAcceptedPriceSchedule,
} = onboardingSlice.actions;
export default onboardingSlice.reducer;
